import { X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MemberProfileFormFields } from './MemberProfileFormFields';
import {
    memberRecordToProfileFormState,
    profileFormStateToUpdatePayload,
} from '../utils/memberProfileForm';

const EMPTY_FORM = memberRecordToProfileFormState({});

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onSubmit: (payload: Record<string, string>) => void | Promise<void>;
 *   submitting?: boolean;
 *   member: import('../api/membersDirectory').mapApiMemberDetailToView extends (...args: unknown[]) => infer R ? R : never | null;
 * }} props
 */
export function EditProfileModal({
    open,
    onClose,
    onSubmit,
    submitting = false,
    member,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState(
        /** @type {Record<string, string>} */ ({}),
    );

    useEffect(() => {
        if (!open || !member) {
            setForm(EMPTY_FORM);
            setError('');
            setFieldErrors({});
            return;
        }
        setForm(memberRecordToProfileFormState(member));
        setError('');
        setFieldErrors({});
    }, [open, member]);

    useEffect(() => {
        if (!open || Object.keys(fieldErrors).length === 0) return;
        document
            .querySelector('.app-modal .is-invalid')
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [fieldErrors, open]);

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !submitting) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose, submitting]);

    if (!open || !member) return null;

    function setField(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        const nextFieldErrors = /** @type {Record<string, string>} */ ({});
        const requiredMessage = t('profilePage.fieldRequired');

        if (!form.firstName.trim()) {
            nextFieldErrors.firstName = requiredMessage;
        }
        if (!form.lastName.trim()) {
            nextFieldErrors.lastName = requiredMessage;
        }
        if (!form.clubJoinDate.trim()) {
            nextFieldErrors.clubJoinDate = requiredMessage;
        }

        if (Object.keys(nextFieldErrors).length > 0) {
            setFieldErrors(nextFieldErrors);
            return;
        }

        setFieldErrors({});

        try {
            await onSubmit(profileFormStateToUpdatePayload(form));
        } catch (err) {
            const msg =
                err && typeof err === 'object' && 'message' in err
                    ? String(/** @type {{ message?: unknown }} */ (err).message)
                    : '';
            setError(msg || t('profilePage.editProfileError'));
        }
    }

    return (
        <div
            className="app-modal-backdrop"
            onClick={submitting ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal app-modal--wide"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="app-modal-header">
                    <h2 id={titleId} className="app-modal-title h6 mb-0">
                        {t('profilePage.editProfileTitle')}
                    </h2>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label={t('profilePage.addChildClose')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="app-modal-body app-modal-body--scroll">
                        <MemberProfileFormFields
                            idPrefix="edit-profile"
                            form={form}
                            setField={setField}
                            submitting={submitting}
                            fieldErrors={fieldErrors}
                        />
                        {error ? (
                            <p
                                className="app-modal-error small text-danger mb-0 mt-3"
                                role="alert"
                            >
                                {error}
                            </p>
                        ) : null}
                    </div>
                    <div className="app-modal-footer">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {t('profilePage.addChildCancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting
                                ? t('profilePage.editProfileSubmitting')
                                : t('profilePage.editProfileSubmit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
